import React from 'react';
import { useNavigate } from 'react-router';

import Button from '~/components/button';
import { valid, required, usAddress } from '~/utils/validation';
import {
  helpCenterRoute,
  myPlanRoute,
  lawnDetailsRoute,
  newLawnRoute,
  zipcodeLawnSizeRoute,
  yardIssuesPageRoute,
} from '~/routes';
import { useSubscriptionsForLawn } from '~/features/subscriptions/subscription-actions';
import { subscriptionType } from '~/features/subscriptions/subscription';
import { useLawn } from '~/features/lawns/lawn-actions';
import { EventType, analyticsBeacon } from '~/utils/analytics';
import { AutocompleteTypes } from '~/components/inputs/autocomplete-address-input/autocomplete-storage';
import AutocompleteAddressField from '~/components/inputs/autocomplete-address-input/autocomplete-address-field';
import { Field, Form } from '~/components/form';

import styles from '~/components/lawns/lawn-autocomplete-form.module.scss';

export const validateAddress = valid(
  required(
    <span>
      Make sure to select an address from the dropdown. If your address doesn't
      appear{' '}
      <a
        style={{ color: 'var(--error-color)' }}
        href={helpCenterRoute()}
        target="_blank"
        rel="noopener noreferrer"
      >
        visit our help center
      </a>{' '}
      for support.
    </span>
  ),
  usAddress()
);

const LawnAutocompleteForm = ({
  buttonText,
  buttonVariant,
  buttonClassname,
  analyticsId,
  type = AutocompleteTypes.FULL_ADDRESS,
  initialValues,
}) => {
  const navigate = useNavigate();
  const { subscriptions, isLoading: isLoadingSubscriptions } =
    useSubscriptionsForLawn();
  const { lawn } = useLawn();

  const showAddressInput = !subscriptions || subscriptions?.length === 0;

  const isLawnSubscriber = subscriptions?.some(
    (subscription) => subscription.planType === subscriptionType.LAWN_PLAN
  );
  const isNonLawnSubscriber = !isLawnSubscriber && subscriptions?.length > 0;

  const navRoutes = (address) => {
    if (isLawnSubscriber) {
      navigate(myPlanRoute());
    } else if (isNonLawnSubscriber) {
      navigate(lawnDetailsRoute(lawn.uuid));
    } else {
      navigate(newLawnRoute(address));
    }
  };

  const isYardFunnelEnabled = true;

  const handleSubmit = async (formValues) => {
    analyticsBeacon.emit(EventType.ADDRESS_CTA_CLICKED, {
      url: window.location.pathname,
      inputId: analyticsId,
      type,
    });

    const { address } = formValues;

    // No `line1` => user went through zip code entry => send them to Zip Code Lawn Size page
    if (!address?.line1) {
      if (isYardFunnelEnabled) {
        return setTimeout(() => {
          navigate(yardIssuesPageRoute(address));
        }, 0);
      } else {
        return navigate(zipcodeLawnSizeRoute(address));
      }
    }

    // Timeout to slightly delay navigation while we send our analytics event
    setTimeout(() => {
      navRoutes(address);
    }, 0);
  };

  if (isLoadingSubscriptions) return null;

  return (
    <Form
      onSubmit={handleSubmit}
      className={styles.lawnAutocompleteForm}
      initialValues={initialValues}
      validate={validateAddressForm(type)}
    >
      {() => (
        <>
          {showAddressInput && (
            <Field
              label={
                type === AutocompleteTypes.FULL_ADDRESS
                  ? 'Home address'
                  : 'Zip code'
              }
              name="address"
              component={AutocompleteAddressField}
              placeholder={
                type === AutocompleteTypes.ZIP_CODE
                  ? 'Start typing to find your zip code...'
                  : 'Start typing to find your home address...'
              }
              type={type}
              initialValue={initialValues?.addressText}
            />
          )}

          <div style={{ textAlign: 'center' }}>
            <Button
              className={buttonClassname}
              type="submit"
              fullWidth
              variant={buttonVariant}
            >
              {buttonText}
            </Button>
          </div>
        </>
      )}
    </Form>
  );
};

export default LawnAutocompleteForm;

const validateAddressForm = (type) => (formValues) => {
  return {
    address: valid(
      required(
        <span>
          Make sure to select{' '}
          {type === AutocompleteTypes.ZIP_CODE ? 'your zip code' : 'an address'}{' '}
          from the dropdown. If your{' '}
          {type === AutocompleteTypes.ZIP_CODE ? 'zip code' : 'address'} doesn't
          appear{' '}
          <a
            style={{ color: 'var(--error-color)' }}
            href={helpCenterRoute()}
            target="_blank"
            rel="noopener noreferrer"
          >
            visit our help center
          </a>{' '}
          for support.
        </span>
      ),
      usAddress()
    )(formValues.address),
  };
};
